import React, { useEffect, useState } from 'react';
import { Star, User, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRow = ({ rating, size = 16 }) => (
  <span className="inline-flex items-center">
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        size={size}
        className={i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ))}
  </span>
);

/**
 * ReviewSection - public reviews block for a product detail page
 * Props: productId
 */
const ReviewSection = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], total: 0, average: 0, histogram: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetch(`${API_URL}/api/products/${productId}/reviews`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <section className="mt-8 bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <MessageSquare size={20} /> Müşteri Yorumları
        </h3>
        <p className="text-gray-500 text-sm">Yükleniyor...</p>
      </section>
    );
  }

  return (
    <section className="mt-8 bg-white rounded-lg shadow-sm p-6" data-testid="product-reviews-section">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <MessageSquare size={20} /> Müşteri Yorumları
        {data.total > 0 && <span className="text-sm font-normal text-gray-500">({data.total})</span>}
      </h3>

      {data.total === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <MessageSquare size={36} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">Henüz yorum yapılmamış. İlk yorumu siz yazın!</p>
          <p className="text-xs text-gray-400 mt-1">Ürünü satın alıp teslim aldıktan sonra yorum yapabilirsiniz.</p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 mb-6 grid md:grid-cols-2 gap-4">
            <div className="text-center md:text-left">
              <div className="text-5xl font-bold text-gray-800">{data.average}</div>
              <StarRow rating={Math.round(data.average)} size={20} />
              <p className="text-sm text-gray-600 mt-1">{data.total} değerlendirme</p>
            </div>
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map(n => {
                const count = data.histogram?.[n] || 0;
                const pct = data.total ? (count / data.total) * 100 : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs">
                    <span className="w-4">{n}</span>
                    <Star size={12} className="fill-yellow-400 text-yellow-400" />
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-2 bg-yellow-400" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-8 text-right text-gray-600">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reviews list */}
          <div className="space-y-4">
            {data.reviews.map(r => (
              <div
                key={r.id}
                className="border-b pb-4 last:border-b-0"
                data-testid={`review-item-${r.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-sm">{r.user_name}</span>
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                        ✓ Doğrulanmış alıcı
                      </span>
                      <span className="text-xs text-gray-400">{fmtDate(r.created_at)}</span>
                    </div>
                    <StarRow rating={r.rating} size={14} />
                    <p className="mt-2 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

/**
 * ReviewFormModal - simple form to submit a review (used on Profile page)
 */
export const ReviewFormModal = ({ item, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (comment.trim().length < 3) {
      setError('Yorum en az 3 karakter olmalı');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken');
      const r = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          product_id: item.product_id,
          order_id: item.order_id,
          rating,
          comment: comment.trim()
        })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Gönderilemedi');
      onSubmitted?.(d.message);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="review-form-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
          <h2 className="font-bold">Ürün Yorumu Yaz</h2>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {item.product_image && (
            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
              <img src={item.product_image} alt="" className="w-14 h-14 rounded object-cover" />
              <div className="text-sm">
                <p className="font-medium text-gray-800 line-clamp-2">{item.product_name}</p>
                <p className="text-xs text-gray-500">Sipariş: {item.order_id}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Puanınız</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`review-star-${n}`}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={32}
                    className={n <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                  />
                </button>
              ))}
              <span className="ml-2 font-bold text-gray-800">{rating}/5</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Yorumunuz</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              maxLength={2000}
              required
              data-testid="review-comment-input"
              placeholder="Ürün hakkındaki deneyiminizi paylaşın..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-y"
            />
            <p className="text-xs text-gray-400 mt-1">{comment.length}/2000 karakter</p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
            💡 Yorumunuz admin onayından sonra yayınlanacaktır.
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="review-submit-btn"
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
            >
              {submitting ? 'Gönderiliyor...' : 'Yorumu Gönder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewSection;
